import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { styles } from "@sunSource/Widgets/HeaderIconLinks";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    color = "color",
    heading = "heading",
    path = "path",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.color]: string;
        [fields.heading]: string;
        [fields.path]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    overlayWrapperStyles?: InjectableCss;
    titleStyles?: InjectableCss;
    linkStyle?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const CustomTitleWrapper = styled.div<{
    backgroundImage: string;
    color: string;
    checkBackground: string;
}>`
    background: ${props =>
        props.checkBackground === "image" ? `url(${props.backgroundImage})` : props.color};
    background-size: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    height:230px;
    width:100%;
    background-position:center;  
    font-size:30px;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(theme, [
            // null,
            css`
                height: 240px;
                width: 100%;
                justify-content: center;
                margin: auto;
            `,
            css`
            height: 130px;
            font-size: 17px;
            text-align: center;
            line-height: 24px;
            `,
            css`
            height: 130px;
            font-size: 17px;
            text-align: center;
            line-height: 24px;
            `,
            css`
            height: 175px;
            font-size: 17px;
            text-align: center;
            line-height: 30px;
            `,
            // null,
            // null,
            null,
        ])}     
`;

const CustomPageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields }: Props) => {
    const links1 = useGetLink(fields.path);
    const overlayWrapperStyles = {
        css: css`
    background-size: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    height:230px;
    width:100%;
    background-position:center;  
    font-size:30px;
         ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // null,
                    css`
                height: 240px;
                width: 100%;
                justify-content: center;
                margin: auto;
                padding: 24px;
            `,
                    css`
            height: 130px;
            font-size: 17px;
            text-align: center;
            line-height: 24px;
            `,
                    css`
            height: 130px;
            font-size: 17px;
            text-align: center;
            line-height: 24px;
            `,
                    css`
            height: 175px;
            font-size: 17px;
            text-align: center;
            line-height: 30px;
            `,
                    null,
                ])}  
        `

    };
    const linkStyle = {
        css: css`
        display: block;
        width: 100%;
        `,

    };


    const titleStyles = {
        css: css`
            color: #fff;
            margin: 0;
            font-size: 24px;
            height: 100%;
            width: 100%;
            background: rgba(0,0,0,0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            text-transform: uppercase;
            /* padding: 0px 20px; */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // null,
                    css`
            line-height: 34px;
            font-size: 20px;
            padding: 20px;
            `,
                    css`
            font-size: 19px;
            text-align: center;
            line-height: 22px;
            `,
                    css`
            font-size: 20px;
            text-align: center;
            line-height: 22px;
            `,
                    css`
            font-size: 26px;
            text-align: center;
            line-height: 27px;
            `,
                    css`
            font-size: 34px;
            text-align: center;
            line-height: 34px;
            padding: 20px;
            `,
                    // null,
                    // null,
                    // null,
                ])} 
        `,
    };
    return (
        <StyledWrapper className="banner-wrapper" {...overlayWrapperStyles}>
            <Link href={links1.url} {...linkStyle}>
                <CustomTitleWrapper
                    backgroundImage={fields.image}
                    color={fields.color}
                    checkBackground={fields.background}
                >
                    {fields.heading ? (
                        <Typography variant="h2" data-test-selector="CustomPageTitle" {...titleStyles}>
                            {fields.heading}
                        </Typography>
                    ) : null}

                </CustomTitleWrapper>
            </Link>
        </StyledWrapper>
    )
}


const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomPageTitle),
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.color,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Custom Page Title",
                defaultValue: "",
            },
            {
                name: fields.path,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],

    },
};

export default widgetModule;